import React from 'react';
import { Col, Row } from 'reactstrap';
import { FormFieldHeader } from '../../../../../../../components/FormFieldHeader';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import { SelectOption } from '../../../../../../../components/Select';
import { LocationService } from '../../../../../../../services/v1/applicant_tracking/LocationService';
import { SinglePaginatedSelect } from '../../../../../../../components/Select/SinglePaginatedSelect';
import { PushToHrisFormValues } from '../../..';
import { InputFormField } from '../../../../../../../components/InputFormField';
import classNames from 'classnames';
import { InfoTooltip } from '../../../../../../../components/InfoTooltip';

interface PropTypes {
  register: UseFormRegister<PushToHrisFormValues>;
  errors: FieldErrors;
  control: Control<PushToHrisFormValues>;
}

interface AdditionalType {
  page: number;
}

const defaultAdditional: AdditionalType = {
  page: 1,
};

function LocationSelect(props: {
  selected: SelectOption;
  disabled: boolean;
  onChange: (opt: SelectOption) => void;
}) {
  const loadOptions = async (
    search: string,
    _loadedOptions: unknown,
    { page }: AdditionalType,
  ) => {
    const result = await LocationService.list({
      page: page,
      name: search,
    });

    return {
      options: result.locations.map(
        (location) =>
          ({
            label: location.name,
            value: location.id.toString(),
          } as SelectOption),
      ),
      hasMore: !!result.nextPage,
      additional: {
        page: page + 1,
      },
    };
  };

  return (
    <SinglePaginatedSelect
      additional={defaultAdditional}
      isClearable={true}
      value={props.selected}
      loadOptions={loadOptions}
      onChange={props.onChange}
      debounceTimeout={300}
      placeholder='Select Location'
      closeMenuOnSelect={true}
    />
  );
}

export function OfficeAndWorkEmailRow(props: PropTypes) {
  return (
    <Row className='mb-3 align-items-end'>
      <Col sm={6}>
        <div className='d-flex gap-2'>
          <FormFieldHeader fieldName={'Office'} isRequired={true} />
          <InfoTooltip>
            If no exact match is found in the HRIS, the closest available option
            will be selected.
          </InfoTooltip>
        </div>
        <Controller
          name='office'
          control={props.control}
          rules={{ required: 'Office is required' }}
          render={({ field }) => (
            <LocationSelect
              selected={field.value}
              onChange={field.onChange}
              disabled={false}
            />
          )}
        />
      </Col>
      <Col sm={6}>
        <InputFormField
          type={'text'}
          registerReturn={props.register('workEmail', {
            required: true,
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Invalid email address',
            },
          })}
          fieldName={'Work Email'}
          placeholder={'Insert Email'}
          isRequired={true}
          classNames={classNames(props.errors.email && 'is-invalid', 'fs-5')}
          tooltipText={
            'A work email is required by your HRIS. Please update if one has already been created.'
          }
        />
      </Col>
    </Row>
  );
}
